import { getEnabledApps } from '~/shell/app/registry'
import { switchApp } from '~/shell/app/switchApp'
import { getProductsConfig } from '~/shell/config'

export interface Product {
  /**
   * Stable product key; also the React list key. For an in-suite product this is the app's id, so the
   * switcher and the URL cannot disagree about what a product is called.
   */
  id: string
  /** Display name shown as the item's bold title (and, for the current product, the switcher trigger label). */
  name: string
  /** One-line description shown under the title in the switcher's detailed item. */
  description: string
  /** Whether this is the product currently being viewed. Drives the switcher's checkmark, nothing else. */
  current: boolean
  /**
   * Whether this product is an app this shell serves, and so reachable by a same-origin route rather than a
   * full-page navigation to another host. A different axis from {@link current}: an in-suite product that is
   * not the active one is still same-origin, which is the point of serving one origin.
   */
  inSuite: boolean
  /**
   * Resolved navigation target: an in-suite product's own app segment under the active customer, or a
   * cross-origin product's configured host.
   */
  href: string
}

/**
 * The set of sibling products the current session may reach, driving the header's product switcher.
 *
 * Every in-suite product comes from the app registry, so an app's display copy is the app's own (see
 * `shared/appContract`) and adding an app adds a switcher entry with no edit here. Reading
 * `getEnabledApps` rather than the whole registry is what keeps a registered-but-unbuilt app out of the
 * switcher.
 *
 * The mirror image of arc's hook of the same name, which is the file this becomes at the merge: here
 * Scout is the app the registry serves and arc is the cross-origin product, so deleting
 * `crossOriginProducts` below is what finishes the merge once one shell serves both.
 *
 * Which products a session may reach is still a stub, matching arc: every enabled app is offered, plus
 * any configured cross-origin product. When the real per-user/customer product-access signal lands (a
 * future outpatient column/claim) it filters this list without changing where the copy comes from — the
 * same seam pattern as `useCanLogout` and `useRoleLabel`.
 */
export const useEnabledProducts = (
  pathname: string,
  activeApp: string | undefined,
): Product[] => {
  const inSuiteProducts: Product[] = getEnabledApps().map((app) => ({
    id: app.id,
    name: app.name,
    description: app.description,
    current: app.id === activeApp,
    inSuite: true,
    href: switchApp(pathname, app.id),
  }))

  const offeredInSuite = new Set(inSuiteProducts.map((product) => product.id))

  return [
    ...inSuiteProducts,
    ...crossOriginProducts().filter(
      (product) => !offeredInSuite.has(product.id),
    ),
  ]
}

/**
 * The products still deployed to their own hosts, which the switcher reaches by full-page navigation.
 *
 * Arc's copy is a literal here rather than registry-supplied because a cross-origin product is not an
 * app: what this shell serves comes from the registry, and this is the list of what it does not. The
 * caller drops an entry the suite already serves, so registering arc here would offer it as an app
 * rather than list it twice.
 *
 * A cross-origin product with no configured host is omitted rather than offered as a dead link.
 */
const crossOriginProducts = (): Product[] => {
  const productsConfig = getProductsConfig()

  if (!productsConfig.arc.enabled || !productsConfig.arc.url) {
    return []
  }

  return [
    {
      id: 'arc',
      name: 'Arc',
      description:
        'Analytics for revenue impact, provider coaching, and encounter history',
      current: false,
      inSuite: false,
      href: productsConfig.arc.url,
    },
  ]
}
